import { useCallback, useEffect, useRef, useState } from 'react';
import { createCustomerETLApi, refreshETLFiles } from '../api/etlServiceApi';

/**
 * Manages state + polling for the step-by-step OCEL generation pipeline.
 *
 * @param {boolean} etlServiceReachable
 * @param {object}  [opts]
 * @param {string|null} [opts.customerId]   — active customer ID
 * @param {Function}    [opts.onAssembleDone]
 * @param {boolean}     [opts.isActiveTab]  — when true, polls step statuses every 8s so the
 *   Pipeline tab stays current even if the viewer didn't trigger the change themselves
 */
export function useStepPipeline(etlServiceReachable, { customerId, onAssembleDone, isActiveTab } = {}) {
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const pollingRef = useRef({});

  const refresh = useCallback(async () => {
    if (!etlServiceReachable || !customerId) return;
    const api = createCustomerETLApi(customerId);
    try {
      const data = await api.getAllStepsStatus();
      setSteps(data);
      setError(null);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) {
        setError('Step endpoints not found — restart the ETL service to pick up the new code.');
      } else if (status === 503) {
        setError('ETL service started but step runner not initialised — check service logs.');
      } else {
        setError(`Failed to load step statuses (${status ?? 'network error'})`);
      }
    } finally {
      setLoading(false);
    }
  }, [etlServiceReachable, customerId]);

  const runStep = useCallback(
    async (stepId, options = {}) => {
      if (!customerId) return;
      const api = createCustomerETLApi(customerId);
      const res = await api.triggerStep(stepId, options);

      setSteps((prev) =>
        prev.map((s) =>
          s.step_id === stepId
            ? { ...s, status: 'running', step_run_id: res.step_run_id, error: null }
            : s,
        ),
      );

      const intervalId = setInterval(async () => {
        try {
          const run = await api.pollStepRun(res.step_run_id);
          setSteps((prev) =>
            prev.map((s) => (s.step_id === stepId ? { ...s, ...run } : s)),
          );
          if (run.status === 'done' || run.status === 'failed') {
            clearInterval(pollingRef.current[res.step_run_id]);
            delete pollingRef.current[res.step_run_id];
            refresh();
            if (stepId === 'assemble' && run.status === 'done') {
              refreshETLFiles().catch(() => {}).finally(() => {
                onAssembleDone?.();
              });
            }
          }
        } catch {
          // transient polling error — ignore
        }
      }, 2000);

      pollingRef.current[res.step_run_id] = intervalId;
      return res;
    },
    [customerId, refresh],
  );

  useEffect(() => {
    if (etlServiceReachable && customerId) {
      setLoading(true);
      refresh();
    } else {
      setSteps([]);
      setLoading(false);
    }
  }, [etlServiceReachable, customerId, refresh]);

  useEffect(
    () => () => Object.values(pollingRef.current).forEach(clearInterval),
    [],
  );

  // Keep step statuses current while the Pipeline tab is visible, even if nothing
  // was triggered from this browser (e.g. a schedule fired, or another tab changed it).
  useEffect(() => {
    if (!isActiveTab || !etlServiceReachable || !customerId) return;
    const intervalId = setInterval(refresh, 8000);
    return () => clearInterval(intervalId);
  }, [isActiveTab, etlServiceReachable, customerId, refresh]);

  return { steps, loading, error, runStep, refresh };
}
